import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';

interface RedeSocial {
  seqConfigRede: number;
  dscUsuario: string;
  datRegistro: string;
  sitRegistro: number;
}

interface TabelaRedeSocialProps {
  listaRedeSocial: RedeSocial[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof RedeSocial; direcao: 'asc' | 'desc' } | null;

const TabelaRedeSocial = ({ listaRedeSocial, listaVazia, loading }: TabelaRedeSocialProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof RedeSocial | null>(null);

  const descricaoRede = location.state?.descricaoRede || 'Descrição Padrão';
  const codigoRede = location.state?.codigoRede || '';

  const manipularOrdenacao = (chave: keyof RedeSocial) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaRedeSocial];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaRedeSocial, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof RedeSocial) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderizarLinhas = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((redeSocial) => (
        <Tr key={redeSocial.seqConfigRede}>
          <Td>{redeSocial.seqConfigRede}</Td>
          <Td>{redeSocial.dscUsuario}</Td>
          <Td>{redeSocial.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{redeSocial.sitRegistro === 1 ? 'Ativo' : 'Inativo'}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() =>
                navigate(`editar-rede-social/${redeSocial.seqConfigRede}`, {
                  state: { descricaoRede, codigoRede },
                })
              }
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={5} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('seqConfigRede')}
              onMouseEnter={() => setColunaDestacada('seqConfigRede')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'seqConfigRede' ? 'blue.500' : 'inherit'}>
                ID{obterIndicadorOrdenacao('seqConfigRede')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscUsuario')}
              onMouseEnter={() => setColunaDestacada('dscUsuario')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscUsuario' ? 'blue.500' : 'inherit'}>
                Descrição{obterIndicadorOrdenacao('dscUsuario')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datRegistro' ? 'blue.500' : 'inherit'}>
                Data de Registro{obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('sitRegistro')}
              onMouseEnter={() => setColunaDestacada('sitRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'sitRegistro' ? 'blue.500' : 'inherit'}>
                Ativo{obterIndicadorOrdenacao('sitRegistro')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={5} color="#999898" textAlign="center">
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderizarLinhas()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaRedeSocial;
